import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useBridge } from '@onl-web/shared/bridge'

import CardFillIcon from '@/assets/icons/card-fill.svg?react'
import { useToast } from '@/components/Toast'
import { CTAButton } from '@/components/ui/CTAButton'
import { Header } from '@/components/ui/Header'
import { addPaymentBreadcrumb } from '@/lib/sentry'
import { readResume, saveResume } from '@/pages/SealedVinylPage/lib/resume'
import { usePostPaymentMethod } from '@/payment'
import type { PostPaymentMethodBody } from '@/payment/api'

import { Radio } from './components'
import { INITIAL_FORM } from './lib/constants'
import { getCardRegisterErrorMessage } from './lib/errorMap'
import { digits, formatCardNumber, formatExpiry } from './lib/utils'
import type { CardFormValues, IdentificationType } from './lib/validation'
import { isValidCardForm } from './lib/validation'
import {
  CardNumberInput,
  CardNumberInputWrapper,
  CardPreview,
  CardPreviewExpiry,
  CardPreviewIcon,
  CardPreviewInfo,
  CardPreviewNumber,
  ClearButton,
  ErrorText,
  FieldRow,
  FormGroup,
  HiddenRadio,
  InputField,
  PageContent,
  PageWrapper,
  RadioGroup,
  RadioRow,
  SubmitBar,
} from './styles'

/**
 * 카드 등록 페이지 — NICEPAY 직접 입력 흐름.
 *
 * Figma 시안: 20690:37410.
 * PaymentStep "결제 카드 추가" placeholder 클릭 → 이 페이지 진입.
 * 등록 성공 시 router.back() — PaymentStep 캐러셀에 새 카드 자동 노출(query invalidate).
 */
export default function CardRegisterPage() {
  const [form, setForm] = useState<CardFormValues>(INITIAL_FORM)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const navigate = useNavigate()
  const { show: showToast } = useToast()
  const { callHandler } = useBridge()
  const mutation = usePostPaymentMethod()

  // 인풋 포커스 시 SubmitBar 숨김 — 키보드가 올라오면서 버튼이 콘텐츠 영역을 가리는 것 방지.
  // blur → focus 전환 시 짧게 깜빡이지 않도록 100ms 디바운스.
  useEffect(() => {
    return () => clearTimeout(blurTimerRef.current)
  }, [])

  const handleInputFocus = () => {
    clearTimeout(blurTimerRef.current)
    setIsInputFocused(true)
  }

  const handleInputBlur = () => {
    blurTimerRef.current = setTimeout(() => setIsInputFocused(false), 100)
  }

  const setIdentificationType = (identificationType: IdentificationType) => {
    setForm((prev) => ({ ...prev, identificationType, idNo: '' }))
  }

  const handleSubmit = async () => {
    setSubmitError(null)

    const body: PostPaymentMethodBody = {
      paymentMethodType: 'CARD',
      pgType: 'NICEPAY',
      isQuickRegistered: false,
      detail: {
        identificationType: form.identificationType,
        cardNo: form.cardNo,
        expYear: form.expYear,
        expMonth: form.expMonth,
        idNo: form.idNo,
        cardPw: form.cardPw,
      },
    }

    addPaymentBreadcrumb('카드 등록 시도', { identificationType: form.identificationType })
    try {
      const response = await mutation.mutateAsync(body)
      addPaymentBreadcrumb('카드 등록 완료', { paymentMethodId: response.paymentMethodId })
      // 등록 성공 → Flutter 에 결제수단 목록 갱신 알림(1회). 브릿지 미준비여도 등록 흐름 막지 않음.
      void callHandler('on_payment_method_refresh').catch(() => {})
      // PaymentStep 이 저장해 둔 resume(step·quantity)에 방금 등록한 카드 id 를 더해
      // navigate(-1) 복귀 시 캐러셀에서 그 카드를 자동선택하게 한다.
      const existing = readResume()
      saveResume({
        step: 'payment',
        quantity: existing?.quantity,
        selectedCardId: response.paymentMethodId != null ? String(response.paymentMethodId) : undefined,
      })
      showToast('카드가 등록됐어요')
      navigate(-1)
    } catch (error) {
      addPaymentBreadcrumb('카드 등록 실패')
      setSubmitError(getCardRegisterErrorMessage(error))
    }
  }

  const isFormValid = isValidCardForm(form)
  const isSubmitting = mutation.isPending
  const isCorporate = form.identificationType === 'BUSINESS'

  return (
    <PageWrapper>
      <Header title='카드 등록' onBack={() => navigate(-1)} />

      <PageContent>
        <CardPreview>
          <CardPreviewInfo>
            <CardPreviewNumber>{form.cardNo ? formatCardNumber(form.cardNo) : ''}</CardPreviewNumber>
            <CardPreviewExpiry>{formatExpiry(form.expMonth, form.expYear)}</CardPreviewExpiry>
          </CardPreviewInfo>
          <CardPreviewIcon aria-hidden='true'>
            <CardFillIcon width={24} height={24} />
          </CardPreviewIcon>
        </CardPreview>

        <RadioGroup role='radiogroup'>
          <RadioRow>
            <HiddenRadio
              type='radio'
              name='identificationType'
              checked={form.identificationType === 'PERSONAL'}
              onChange={() => setIdentificationType('PERSONAL')}
            />
            <Radio selected={form.identificationType === 'PERSONAL'} />
            개인카드
          </RadioRow>
          <RadioRow>
            <HiddenRadio
              type='radio'
              name='identificationType'
              checked={form.identificationType === 'BUSINESS'}
              onChange={() => setIdentificationType('BUSINESS')}
            />
            <Radio selected={form.identificationType === 'BUSINESS'} />
            법인카드
          </RadioRow>
        </RadioGroup>

        <FormGroup
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        >
          <CardNumberInputWrapper>
            <CardNumberInput
              aria-label='카드 번호'
              inputMode='numeric'
              placeholder='카드 번호'
              value={formatCardNumber(form.cardNo)}
              onChange={(e) => setForm({ ...form, cardNo: digits(e.target.value, 16) })}
              maxLength={19}
            />
            {form.cardNo && (
              <ClearButton type='button' aria-label='카드 번호 지우기' onClick={() => setForm({ ...form, cardNo: '' })}>
                ×
              </ClearButton>
            )}
          </CardNumberInputWrapper>
          <FieldRow>
            <InputField
              aria-label='유효기간 MM/YY'
              inputMode='numeric'
              placeholder='MM/YY'
              value={formatExpiry(form.expMonth, form.expYear)}
              onChange={(e) => {
                const raw = digits(e.target.value, 4)
                setForm({ ...form, expMonth: raw.slice(0, 2), expYear: raw.slice(2, 4) })
              }}
              maxLength={5}
            />
            <InputField
              aria-label='비밀번호 앞 2자리'
              inputMode='numeric'
              type='password'
              placeholder='비밀번호 앞 2자리'
              value={form.cardPw}
              onChange={(e) => setForm({ ...form, cardPw: digits(e.target.value, 2) })}
              maxLength={2}
            />
          </FieldRow>
          <InputField
            aria-label={isCorporate ? '사업자등록번호 10자리' : '주민등록번호 앞 6자리'}
            inputMode='numeric'
            placeholder={isCorporate ? '사업자등록번호 10자리' : '주민등록번호 앞 6자리'}
            value={form.idNo}
            onChange={(e) => setForm({ ...form, idNo: digits(e.target.value, isCorporate ? 10 : 6) })}
            maxLength={isCorporate ? 10 : 6}
          />
          {submitError && <ErrorText role='alert'>{submitError}</ErrorText>}
        </FormGroup>
      </PageContent>

      {!isInputFocused && (
        <SubmitBar>
          <CTAButton variant='brand' loading={isSubmitting} disabled={!isFormValid} onClick={handleSubmit}>
            카드 추가
          </CTAButton>
        </SubmitBar>
      )}
    </PageWrapper>
  )
}
